import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert, Box, Button, Chip, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { MaintenanceService } from '../../services/maintenance.service';
import { useToast } from '../../context/ToastContext';
import { EquipmentImportResult } from '../../interfaces/maintenance';

const TEMPLATE = 'code,name,type_code,manufacturer,model,serial_no,status,criticality,ownership,owner_party,rental_until,location,parent_code,fixed_asset_code,commissioned_on,warranty_until,notes\r\n';

/** CSV import: validate first (dry run), then import. All-or-nothing on the server. */
const EquipmentImport: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [file, setFile] = useState<File | null>(null);
    const [result, setResult] = useState<EquipmentImportResult | null>(null);
    const [busy, setBusy] = useState(false);

    const run = async (dryRun: boolean) => {
        if (!file) { toast.showError('Choose a CSV file'); return; }
        try {
            setBusy(true);
            const r = await MaintenanceService.importEquipment(institutionId, file, dryRun);
            setResult(r);
            if (!dryRun) toast.showSuccess(`${r.createdRows} equipment created`);
        } catch (e) {
            toast.showApiError(e, dryRun ? 'Failed to validate file' : 'Failed to import file');
        } finally {
            setBusy(false);
        }
    };

    const downloadTemplate = () => {
        const url = URL.createObjectURL(new Blob([TEMPLATE], { type: 'text/csv' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'equipment-template.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    const allValid = !!result && result.dryRun && result.totalRows > 0 && result.validRows === result.totalRows;

    return (
        <Box sx={{ p: { xs: 1, sm: 3 } }} data-help-page="maint.equipmentImport.page">
            <Typography variant="h5" sx={{ mb: 2 }}>Import equipment</Typography>
            <Paper sx={{ p: 2, mb: 2 }} data-help-section="maint.equipmentImport.upload">
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Required columns: code, name, type_code. Optional: manufacturer, model, serial_no, status, criticality, ownership, owner_party,
                    rental_until, location (department name or code), parent_code, fixed_asset_code, commissioned_on,
                    warranty_until, notes. The file is validated first; nothing is created unless every row is valid.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <Button component="label" variant="outlined" data-help="maint.equipmentImport.chooseFile">
                        {file ? file.name : 'Choose CSV file'}
                        <input
                            hidden type="file" accept=".csv,text/csv" data-testid="csv-input" aria-label="CSV file"
                            onChange={e => { setFile(e.target.files?.[0] ?? null); setResult(null); }}
                        />
                    </Button>
                    <Button data-help="maint.equipmentImport.template" onClick={downloadTemplate}>Download template</Button>
                    <Button data-help="maint.equipmentImport.validate" variant="outlined" disabled={!file || busy} onClick={() => run(true)}>Validate</Button>
                    <Button data-help="maint.equipmentImport.import" variant="contained" disabled={!allValid || busy} onClick={() => run(false)}>Import</Button>
                    <Button onClick={() => navigate('/maintenance/equipment')} data-help="maint.equipmentImport.back">Back</Button>
                </Box>
            </Paper>

            {result && (
                <Paper sx={{ p: 2 }} data-help-section="maint.equipmentImport.result">
                    <Alert severity={result.dryRun ? (allValid ? 'success' : 'warning') : 'success'} sx={{ mb: 2 }}>
                        {result.dryRun
                            ? `${result.validRows} of ${result.totalRows} rows valid`
                            : `${result.createdRows} of ${result.totalRows} rows imported`}
                    </Alert>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow><TableCell data-help="maint.equipmentImport.col.row">Row</TableCell><TableCell data-help="maint.equipmentImport.col.code">Code</TableCell><TableCell data-help="maint.equipmentImport.col.result">Result</TableCell><TableCell data-help="maint.equipmentImport.col.message">Message</TableCell></TableRow>
                            </TableHead>
                            <TableBody>
                                {result.rows.map(r => (
                                    <TableRow key={r.row}>
                                        <TableCell>{r.row}</TableCell>
                                        <TableCell>{r.code}</TableCell>
                                        <TableCell><Chip size="small" color={r.ok ? 'success' : 'error'} label={r.ok ? 'OK' : 'Error'} /></TableCell>
                                        <TableCell>{r.message}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default EquipmentImport;
